/**
 * Browsing Context Tracker
 * 
 * Tracks user browsing behavior for Hyper Realism Mode
 */

interface BrowsingContext {
  currentPage: string;
  browsingHistory: string[];
  sessionDuration: number;
  idleTime: number;
  lastActivity: number;
}

class BrowsingContextTracker {
  private context: BrowsingContext = {
    currentPage: '',
    browsingHistory: [],
    sessionDuration: 0,
    idleTime: 0,
    lastActivity: Date.now(),
  };

  private sessionStartTime: number = Date.now();
  private idleTimer: NodeJS.Timeout | null = null;
  private historyMaxLength = 20;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initialize();
    }
  }

  private initialize() {
    // Track page changes
    this.updateCurrentPage(window.location.pathname);

    // Track user activity
    this.setupActivityListeners();

    // Update session duration every second
    setInterval(() => {
      this.context.sessionDuration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
    }, 1000);
  }

  private setupActivityListeners() {
    const resetIdleTimer = () => {
      this.context.lastActivity = Date.now();
      this.context.idleTime = 0;

      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }

      // Start idle timer (5 minutes)
      this.idleTimer = setTimeout(() => {
        this.updateIdleTime();
      }, 5 * 60 * 1000);
    };

    // Listen for user activity
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, resetIdleTimer, { passive: true });
    });

    // Initial timer
    resetIdleTimer();
  }

  private updateIdleTime() {
    const now = Date.now();
    this.context.idleTime = Math.floor((now - this.context.lastActivity) / 1000);
  }

  updateCurrentPage(path: string) {
    if (this.context.currentPage !== path) {
      // Add previous page to history
      if (this.context.currentPage) {
        this.context.browsingHistory.unshift(this.context.currentPage);
        
        // Limit history length
        if (this.context.browsingHistory.length > this.historyMaxLength) {
          this.context.browsingHistory = this.context.browsingHistory.slice(0, this.historyMaxLength);
        }
      }

      this.context.currentPage = path;
    }
  }

  getContext(): BrowsingContext {
    this.updateIdleTime();
    return { ...this.context };
  }

  getContextSummary(): string {
    const ctx = this.getContext();
    const parts: string[] = [];

    // Current page
    if (ctx.currentPage) {
      parts.push(`Currently viewing: ${ctx.currentPage}`);
    }

    // Session duration
    const minutes = Math.floor(ctx.sessionDuration / 60);
    if (minutes > 0) {
      parts.push(`Session duration: ${minutes} minutes`);
    }

    // Idle time
    const idleMinutes = Math.floor(ctx.idleTime / 60);
    if (idleMinutes > 0) {
      parts.push(`Idle for: ${idleMinutes} minutes`);
    }

    // Recent pages
    if (ctx.browsingHistory.length > 0) {
      const recentPages = ctx.browsingHistory.slice(0, 3).join(', ');
      parts.push(`Recently visited: ${recentPages}`);
    }

    return parts.join('. ');
  }

  reset() {
    this.context = {
      currentPage: window.location.pathname,
      browsingHistory: [],
      sessionDuration: 0,
      idleTime: 0,
      lastActivity: Date.now(),
    };
    this.sessionStartTime = Date.now();
  }
}

// Singleton instance
let trackerInstance: BrowsingContextTracker | null = null;

export function getBrowsingContextTracker(): BrowsingContextTracker {
  if (!trackerInstance) {
    trackerInstance = new BrowsingContextTracker();
  }
  return trackerInstance;
}

export type { BrowsingContext };
